import React, { useState, useEffect } from 'react';
import { quoteService } from '../../api/quoteService';
import { productService } from '../../api/productService';
import { companySettingsService } from '../../api/companySettingsService';
import Autocomplete from '../Common/Autocomplete';
import '../Common/Common.css';

function QuoteForm({ quote, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: '',
  });
  const [items, setItems] = useState([{ product_id: '', quantity: 1, unit_price: '' }]);
  const [products, setProducts] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    if (quote) {
      setFormData({
        client_name: quote.client_name || '',
        client_email: quote.client_email || '',
        client_phone: quote.client_phone || '',
        notes: quote.notes || '',
      });
      if (quote.items && quote.items.length > 0) {
        setItems(
          quote.items.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
          }))
        );
      }
    }
  }, [quote]);

  const fetchProducts = async () => {
    try {
      const response = await productService.getProducts({ per_page: 1000 });
      setProducts(response.data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const settings = await companySettingsService.getSettings();
      setCompanySettings(settings);
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Auto-fill price when product is selected
    if (field === 'product_id' && value) {
      const product = products.find((p) => p.id === parseInt(value));
      if (product) {
        newItems[index].unit_price = product.price;
      }
    }

    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, { product_id: '', quantity: 1, unit_price: '' }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const itemTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      return sum + itemTotal;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate items
    const validItems = items.filter((item) => item.product_id && item.quantity && item.unit_price);
    if (validItems.length === 0) {
      setError('Veuillez ajouter au moins un article avec produit, quantité et prix');
      setLoading(false);
      return;
    }

    const total = calculateTotal();

    try {
      const payload = {
        ...formData,
        total: total.toFixed(2),
        items: validItems,
      };

      if (quote) {
        await quoteService.updateQuote(quote.id, payload);
      } else {
        await quoteService.createQuote(payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de l\'enregistrement du devis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
      {error && <div className="error-message">{error}</div>}

      {/* Company Info Display */}
      {companySettings && (
        <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
          <div style={{ padding: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Informations de l'entreprise</h3>
            <p style={{ margin: '4px 0', fontSize: '14px' }}><strong>{companySettings.company_name}</strong></p>
            {companySettings.company_address && <p style={{ margin: '2px 0', fontSize: '13px' }}>{companySettings.company_address}</p>}
            {companySettings.company_city && companySettings.company_country && (
              <p style={{ margin: '2px 0', fontSize: '13px' }}>
                {companySettings.company_city}, {companySettings.company_country}
              </p>
            )}
            {companySettings.company_phone && <p style={{ margin: '2px 0', fontSize: '13px' }}>Téléphone : {companySettings.company_phone}</p>}
            {companySettings.company_email && <p style={{ margin: '2px 0', fontSize: '13px' }}>Email : {companySettings.company_email}</p>}
          </div>
        </div>
      )}

      {/* Client Information */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h2>Informations du client</h2>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <label>Nom du client *</label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Téléphone *</label>
              <input
                type="text"
                name="client_phone"
                value={formData.client_phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Notes</label>
              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Notes optionnelles..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quote Items */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h2>Articles du devis</h2>
          <button type="button" onClick={handleAddItem} className="btn btn-small btn-success">
            Ajouter un article
          </button>
        </div>
        <div className="card-body">
          {items.map((item, index) => (
            <div
              key={index}
              style={{
                border: '1px solid #e1e8ed',
                padding: '16px',
                borderRadius: '6px',
                marginBottom: '12px',
              }}
            >
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Produit *</label>
                  <Autocomplete
                    options={products.map(p => ({
                      id: p.id,
                      displayName: `${p.name} (${parseFloat(p.price).toFixed(2)} DH)`
                    }))}
                    value={item.product_id}
                    onChange={(value) => handleItemChange(index, 'product_id', value)}
                    placeholder="Rechercher un produit..."
                    displayKey="displayName"
                    valueKey="id"
                  />
                </div>

                <div className="form-group">
                  <label>Quantité *</label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Prix unitaire *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Total ligne</label>
                  <input
                    type="text"
                    value={`${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)} DH`}
                    disabled
                    style={{ background: '#f0f0f0' }}
                  />
                </div>
              </div>

              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="btn btn-small btn-delete"
                  style={{ marginTop: '8px' }}
                >
                  Supprimer l'article
                </button>
              )}
            </div>
          ))}

          <div
            style={{
              marginTop: '20px',
              padding: '16px',
              background: '#f8f9fa',
              borderRadius: '6px',
              textAlign: 'right',
            }}
          >
            <h3 style={{ margin: 0, fontSize: '20px', color: '#2c3e50' }}>
              Total : {calculateTotal().toFixed(2)} DH
            </h3>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Enregistrement...' : quote ? 'Modifier le devis' : 'Créer le devis'}
        </button>
      </div>
    </form>
  );
}

export default QuoteForm;
